import React, { useCallback, useState } from 'react'
import * as yup from 'yup'
import { isEmpty, pathOr } from 'ramda'
import { FormErrors } from '../../..'
import SearchProperties from './SearchProperties'
import { FormikProps, withFormik } from 'formik'
import PropertyPreview from './PropertyPreview'
import { IPropertyFormValues } from './types'
import { useSelector } from 'react-redux'
import { apiStateSelectors } from 'modules/api/apiState'
import { propertyApiEvents, propertyApiSelectors } from 'modules/api/property'
import { getSelectedLightstoneProperty, getSelectedPlace, getSelectedProperty } from 'modules/ui/ChatBot'
import { portfolioApiEvents, portfolioApiSelectors } from 'modules/api/portfolio'

type RequestPayload = {
  placeId: string | null
  propertyId: string | null
  lightstonePropertyId: string
  propertyDescription: string
  unitNumber: string
  buildingName: string
  schemeNumber: string
  schemeName: string
}

export type PropertyConfirmationValues = Pick<
  IPropertyFormValues,
  'propertyDescription' | 'buildingName' | 'unitNumber' | 'schemeNumber' | 'schemeName'
>

interface IPropertyFormProps {
  initialValues: IPropertyFormValues
  isActivePortfolio: boolean
  isDraftPortfolio: boolean
  isReadonly: boolean
  clearSelection: () => void
  handleConfirmation: (values: PropertyConfirmationValues) => void
  redirect: (url: string) => void
  portfolioId: string
}

const validationSchema = yup.object().shape({
  propertyDescription: yup.string().max(20, 'must be 20 characters or less.'),
})

const PropertyForm: React.FC<IPropertyFormProps & FormikProps<IPropertyFormValues>> = ({
  initialValues,
  handleConfirmation,
  isReadonly,
  clearSelection,
  values,
  errors,
  dirty,
  handleChange,
  handleBlur,
  isActivePortfolio,
  isDraftPortfolio,
  redirect,
  portfolioId,
}) => {
  const [requestPayload, setRequestPayload] = useState<RequestPayload | null>(null)

  const placeId = useSelector(getSelectedPlace)
  const propertyId = useSelector(getSelectedProperty)
  const lightstonePropertyId = useSelector(getSelectedLightstoneProperty)

  const _handleConfirmation = useCallback(
    (
      selectedPlace: string,
      selectedProperty: string,
      selectedLightstoneProperty: string,
      propertyDescription: string,
      buildingName: string,
      unitNumber: string,
      schemeNumber: string,
      schemeName: string,
    ) => {
      setRequestPayload({
        placeId: selectedPlace || placeId,
        propertyId: selectedProperty || propertyId,
        lightstonePropertyId: selectedLightstoneProperty || lightstonePropertyId,
        propertyDescription,
        unitNumber,
        buildingName,
        schemeNumber,
        schemeName,
      })
      handleConfirmation({
        propertyDescription,
        buildingName,
        unitNumber,
        schemeNumber,
        schemeName,
      })
    },
    [placeId, propertyId, lightstonePropertyId, handleConfirmation],
  )

  const getGeneralFormErrorsByEvent = useSelector(apiStateSelectors.getGeneralFormErrorsByEvent)
  const getFormFieldErrorsByEvent = useSelector(apiStateSelectors.getFormFieldErrorsByEvent)

  /**
   * TODO: fix field errors. Uses different event now.
   * Nothing dispatches `createProperty_request` any more (confirmation goes through the ChatBot's
   * amend-property / create-portfolio flow), so this lookup can never find anything. It used to
   * build the event from `requestPayload`, which is not a create-property payload at all; the
   * uncalled creator returns every bucket for the event instead — the same empty result.
   */
  const generalErrors =
    requestPayload === null ? [] : getGeneralFormErrorsByEvent(propertyApiEvents.createProperty_request)
  const fieldErrors = requestPayload === null ? {} : getFormFieldErrorsByEvent(propertyApiEvents.createProperty_request)

  const createPortfolioErrors = useSelector(state =>
    apiStateSelectors.getGeneralFormErrorsByEvent(state)(portfolioApiEvents.createPortfolio_request),
  )

  // Every property originates from either a Google place or the deeds office. When the selection is an
  // existing property (picked from search, or the property on a loaded lease) that origin lives on the
  // property record rather than on the selection itself.
  const selectedPropertyPlaceId = useSelector(state => propertyApiSelectors.getPropertyPlaceId(state)(propertyId))
  const selectedPropertyLightstoneId = useSelector(state =>
    propertyApiSelectors.getPropertyLightstoneId(state)(propertyId),
  )

  // Picking a different property swaps `initialValues`, and `enableReinitialize` resets Formik's baseline
  // to match — so `dirty` stays false and can't be relied on to detect a property change. Compare the
  // selection against the property the lease is currently on instead.
  const currentPropertyId = useSelector(state => portfolioApiSelectors.getPropertyIdByPortfolioId(state)(portfolioId))
  const isDifferentPropertySelected = Boolean(
    placeId || lightstonePropertyId || (propertyId && propertyId !== currentPropertyId),
  )

  const getSelectedPropertyType = () => {
    if (lightstonePropertyId || selectedPropertyLightstoneId) {
      return 'Deeds Office'
    } else if (placeId || selectedPropertyPlaceId) {
      return 'Google street address'
    }
    return ''
  }

  return (
    <span>
      {generalErrors.length === 0 && isEmpty(fieldErrors) && values.streetAddress ? (
        <>
          <FormErrors errors={createPortfolioErrors} />
          <PropertyPreview
            initialValues={initialValues}
            values={values}
            errors={errors}
            dirty={dirty || isDifferentPropertySelected}
            handleChange={handleChange}
            handleBlur={handleBlur}
            clearSelection={clearSelection}
            handleConfirmation={(e: React.FormEvent<HTMLFormElement>) => {
              e.preventDefault()
              if (errors.propertyDescription) {
                return
              }
              const placeId = pathOr<string>('', ['selectedPlace', 'placeId'], values)
              const propertyId = pathOr<string>('', ['selectedProperty', 'propertyId'], values)
              const lightstonePropertyId = pathOr<string>('', ['selectedLightstoneProperty', 'lightstoneId'], values)
              _handleConfirmation(
                placeId,
                propertyId,
                lightstonePropertyId,
                values.propertyDescription,
                values.buildingName,
                values.unitNumber,
                values.schemeNumber,
                values.schemeName,
              )
            }}
            isDraftPortfolio={isDraftPortfolio}
            selectedPropertyType={getSelectedPropertyType()}
          />
        </>
      ) : (
        <>
          <SearchProperties
            isReadonly={isReadonly}
            isDraftPortfolio={isDraftPortfolio}
            redirect={redirect}
            portfolioId={portfolioId}
          />
        </>
      )}
    </span>
  )
}

export default withFormik<IPropertyFormProps, IPropertyFormValues>({
  enableReinitialize: true,
  mapPropsToValues: props => ({ ...props.initialValues }),
  validationSchema: validationSchema,
  validateOnChange: true,
  handleSubmit: () => undefined,
})(PropertyForm)
